import React, { useEffect, useMemo, useState } from 'react';
import { styles } from '../../../Util/Modals/Styles';
import './ModalPendingEligibility.scss';
import { useTranslation } from 'react-i18next';
import BaseModal from '../../../Util/Modals/BaseModal';
import CloseCrossButton from '../../../Util/Buttons/CloseCrossButton/CloseCrossButton';
import TableModalPendingEligibility from './TableModalPendingEligibility';
import { ReactComponent as ListViewIcon } from '../../../../assets/icon_button_lista.svg';
import { ReactComponent as OclockIcon } from '../../../../assets/clock-icon.svg';
import { ReactComponent as SearchIcon } from '../../../../assets/search.svg';
import { InputAdornment, TextField } from '@material-ui/core';

type Props = {
  isOpen: boolean;
  style?: ReactModal.Styles;
  className?: string;
  onRequestClose?: () => void;
  shouldCloseOnOverlayClick?: boolean;
  closeModal: any;
  data?: any;
  errorFromParent?: boolean;     
  loadingClauses?: boolean;  
  onRetryFetch?: () => Promise<void>;
};

const ModalPendingEligibility = ({
  isOpen,
  closeModal,
  data,
  errorFromParent = false,
  loadingClauses = false,
  onRetryFetch,
}: Props) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) setSearchTerm('');
  }, [isOpen]);

  const parseDate = (v: any) => {
    if (!v) return null;
    const d = typeof v === 'string' ? new Date(v) : v;
    return isNaN(+d) ? null : d;
  };
  const fmtDate = (v: any) => {
    const d = parseDate(v);
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const norm = (s: any) =>
    String(s ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const handleRetry = () => onRetryFetch?.();

  const filteredClauses: any[] = useMemo(() => {
    const list: any[] = data?.clauses ?? [];
    if (!list.length) return [];
    const q = norm(searchTerm);
    if (!q.trim()) return list;

    return list.filter((c: any) => {
      const clauseNumber = c.clauseNumber ?? c.number ?? '';
      const name = c.name ?? c.title ?? '';
      const category = c.categoryName ?? c.category ?? '';
      const expDate = fmtDate(c.currentExpirationDate ?? c.expiration ?? c.expirationDate ?? null);
      const apprDate = fmtDate(c.approvalDate ?? c.approvedAt ?? null);
      const state = c.state ?? c.status ?? '';

      const haystack = [clauseNumber, name, category, expDate, apprDate, state]
        .map(norm)
        .join(' ');

      return haystack.includes(q);
    });
  }, [data, searchTerm]);

  const loading = !!loadingClauses;
  const loadError = !!errorFromParent;

  return (
    <BaseModal isOpen={isOpen} style={styles} className="modal-pcr">
      <div className="modal-header-container">
        <div className="modal-header-top">
          <span className="pill-code">{data?.project?.code || '--'}</span>
          <CloseCrossButton handleClick={closeModal} />
        </div>
        <h3 className="modal-title">{data?.project?.title || '-'}</h3>
        <div className="modal-summary-row">
          <div className="summary-left">
            <div className="summary-group">
              <ListViewIcon className="summary-icon" />
              <span className='clauses_pending_title'>
                {' '}
                <strong>{data?.project?.clauses}</strong> / {data?.project?.totalClauses || 0}{' '}
                {t('new_column_clauses_pending')}
              </span>
            </div>
            <div className="summary-group">
              <OclockIcon className="summary-icon" />
              <span className='clauses_pending_title'>
                {' '}
                {data?.project?.daysPassed} {t('new_column_days_pending')}
              </span>
            </div>
          </div>
          <TextField
            placeholder={`${t('new_search_by_word')}`}
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="search-icon" style={{ color: '#6c757d' }} />
                </InputAdornment>
              ),
              classes: { notchedOutline: 'custom-outline', root: 'custom-input-root' },
            }}
            className="search-input"
          />
        </div>
      </div>

      <div className="table-pcr">
        <TableModalPendingEligibility
          clausesData={filteredClauses}
          loading={loading}
          error={loadError}
          noResults={!!searchTerm && filteredClauses.length === 0}
          onRetry={handleRetry}
          onCancel={closeModal}
          searchText={searchTerm}
        />
      </div>
    </BaseModal>
  );
};

export default ModalPendingEligibility;
